import { useCallback, useEffect, useRef, useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import { deepPurple } from "@mui/material/colors";
import CustomAvatar from "../../../../../../views/components/common/avatar/Avatar";
import SendIcon from "@mui/icons-material/Send";
import Message from "./Message";
import {
  Box,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress,
  useTheme,
  ButtonBase,
} from "@mui/material";
import { Messages } from "../../../../../../models/Messages/Message";
import { useStores } from "../../../../use-stores";
import { useNotification } from "../../../../../../utils/NotificationsContext";
import { observer } from "mobx-react-lite";
import "../../../../../../styles/chat.css";

const AllConversation = () => {
  const { chatStore, userStore } = useStores();
  const { showError } = useNotification();
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [inputText, setInputText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [overlayVisible, setOverlayVisible] = useState(false);

  const CHARACHTER_LIMIT = 200;

  const scrollToBottom = (val: boolean, refresh: boolean) => {
    if (val || refresh) {
      if (lastMessageRef.current) {
        lastMessageRef.current.scrollIntoView({ behavior: "auto" });
      }
    } else {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop =
          chatContainerRef.current.scrollHeight;
      }
    }
  };

  const fetchMessages = async (pageNumber: number) => {
    if (loading || loadingMore) return;

    pageNumber === 1 ? setLoading(true) : setLoadingMore(true);

    const params = { taskId: chatStore.currentTaskId!, page: pageNumber };

    chatStore
      .getTaskMessages(params)
      .then(() => {
        chatStore.page = pageNumber;

        const timer = setTimeout(() => {
          if (pageNumber === 1) {
            scrollToBottom(false, false);
          }
          setOverlayVisible(false);
        }, 500);
        if (pageNumber === 1) {
          scrollToBottom(true, true);
        }
        return () => clearTimeout(timer);
      })
      .catch((error) => {
        showError("Error", "Error loading messages");
      })
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    setOverlayVisible(true);
    fetchMessages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatStore.currentTaskId]);

  const fetchMoreMessages = async () => {
    if (loadingMore || !chatStore.hasMoreMessages) return;
    fetchMessages(chatStore.page + 1);
  };

  const handleSend = async (inputText: string) => {
    if (inputText?.trim() === "" || inputText?.length > CHARACHTER_LIMIT)
      return;

    try {
      const params = {
        task_id: chatStore.currentTaskId,
        message: inputText,
      };

      const now = new Date();

      const newMessage: Messages = {
        id: 0,
        sender_id: userStore.user?.id!,
        message: inputText,
        sender_name: userStore.user?.name!,
        time: now.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "numeric",
          hour12: true,
        }),
        created_date: new Date().toISOString(),
      };

      chatStore.addMessage(params, newMessage).then(() => {
        scrollToBottom(true, true);
      });
    } catch (error) {
      showError("Error", "Failed to send message");
    }
  };

  const handleScroll = useCallback(() => {
    if (!chatContainerRef.current || loadingMore) return;

    const scrollTop = chatContainerRef.current.scrollTop;

    if (scrollTop === 0 && chatStore.hasMoreMessages) {
      fetchMoreMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingMore, chatStore.hasMoreMessages]);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (chatContainer) {
        chatContainer.removeEventListener("scroll", handleScroll);
      }
    };
  }, [loadingMore, handleScroll]);

  const showErrorMessage = (message: string) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage("");
    }, 3000);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          backgroundColor:
            theme.palette.mode === "dark" ? "#1A1A1A" : "#F4F2FF",
        }}
      >
        {overlayVisible && (
          <Box
            className="chat-overlay"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor:
                theme.palette.mode === "dark" ? "#1A1A1A" : "#F4F2FF",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              maxHeight: "100%",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        )}
        <Box>
          <List
            className="message-list"
            component="div"
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1A1A1A" : "#F4F2FF",
              p: 0,
              overflowX: "hidden",
              msOverflowY: "scroll",
            }}
            ref={chatContainerRef}
          >
            {loadingMore && (
              <Box
                sx={{ display: "flex", justifyContent: "center", padding: 1 }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {Object.entries(chatStore.messagesByDate)?.map(
              ([date, messages]) => (
                <Box key={date} sx={{ textAlign: "center", my: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontWeight: "bold",
                      borderRadius: "20px",
                      display: "inline-block",
                      padding: "8px 16px",
                    }}
                  >
                    {date}
                  </Typography>
                  {messages?.map((msg: any, index: number) => (
                    <ListItem
                      key={msg.id}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent:
                          msg.sender_id === userStore.user?.id
                            ? "flex-end"
                            : "flex-start",
                        paddingLeft: "4px",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {msg.sender_id !== userStore.user?.id && (
                        <ListItemAvatar
                          sx={{ minWidth: "40px", marginTop: "5px" }}
                        >
                          <CustomAvatar backgroundColor={deepPurple[500]} />
                        </ListItemAvatar>
                      )}

                      <Box
                        sx={{
                          maxWidth: "65%",
                          color: theme.palette.text.primary,
                          borderRadius:
                            msg.sender_id === userStore.user?.id
                              ? "10px 0px 10px 10px"
                              : "0px 10px 10px 10px",
                          padding: "8px 12px",
                          margin: "4px 0px",
                          fontWeight: "600",
                          display: "flex",
                          flexDirection: "column",
                          position: "relative",
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? msg.sender_id === userStore.user?.id
                                ? "#2A2A2A"
                                : "#2A2A2A"
                              : msg.sender_id === userStore.user?.id
                              ? "#dbfaf4"
                              : "#E1DDF9",
                          "::before": {
                            content: '""',
                            position: "absolute",
                            width: 0,
                            height: 0,
                            borderStyle: "solid",
                            borderWidth:
                              msg.sender_id === userStore.user?.id
                                ? "4px 0px 10px 10px"
                                : "4px 10px 10px 0px",
                            borderColor:
                              theme.palette.mode === "dark"
                                ? msg.sender_id === userStore.user?.id
                                  ? "transparent transparent transparent #2A2A2A"
                                  : "transparent #2A2A2A transparent transparent"
                                : msg.sender_id === userStore.user?.id
                                ? "transparent transparent transparent #dbfaf4"
                                : "transparent #E1DDF9 transparent transparent",
                            top: 0,
                            left:
                              msg.sender_id === userStore.user?.id
                                ? "auto"
                                : "-8px",
                            right:
                              msg.sender_id === userStore.user?.id
                                ? "-8px"
                                : "auto",
                          },
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "600",
                            fontSize: "12px",
                            paddingBottom: "4px",
                            textAlign:
                              msg.sender_id === userStore.user?.id
                                ? "right"
                                : "left",
                            color: theme.palette.text.primary,
                          }}
                        >
                          {msg.sender_name}
                        </Typography>

                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: "600",
                            fontSize: "13px",
                            color: theme.palette.text.primary,
                          }}
                        >
                          <Message message={msg.message} />
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "right",
                            color: theme.palette.text.disabled,
                            fontWeight: "600",
                            fontSize: "10px",
                            paddingTop: "4px",
                          }}
                        >
                          {msg.created_date
                            ? new Date(msg.created_date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )
                            : "NA"}
                        </Typography>
                      </Box>

                      {index === messages.length - 1 && (
                        <div ref={lastMessageRef} />
                      )}
                    </ListItem>
                  ))}
                </Box>
              )
            )}
            {!loading &&
              !loadingMore &&
              !overlayVisible &&
              Object.entries(chatStore.messagesByDate)?.length === 0 && (
                <Typography
                  textAlign="center"
                  mt={2}
                  sx={{ color: theme.palette.text.secondary }}
                >
                  No messages found
                </Typography>
              )}
          </List>
          <Box sx={{ display: "flex", justifyContent: "center" }} mt={4}>
            {errorMessage && (
              <Typography
                className="error-message"
                textAlign="center"
                variant="caption"
                sx={{ color: "red", zIndex: 1000 }}
              >
                {`Maximum ${CHARACHTER_LIMIT} characters allowed`}
              </Typography>
            )}
          </Box>
        </Box>

        {!overlayVisible && (
          <Box
            className="message-input"
            sx={{
              width: "-webkit-fill-available",
              position: "absolute",
              bottom: "0",
              zIndex: "10",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1a1a1a" : "#F4F2FF",
              paddingRight: "6px",
            }}
          >
            {!overlayVisible &&
              Object.entries(chatStore.messagesByDate)?.length > 0 && (
                <Box sx={{ display: "flex" }}>
                  <ButtonBase
                    onClick={() => fetchMessages(1)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      width: "100%",
                      borderTop: "1px solid #ddd",
                      padding: "2px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1A1A1A" : "#F4F2FF",
                      },
                      "&:active": {
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1A1A1A" : "#F4F2FF",
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.8rem",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Refresh
                    </Typography>
                  </ButtonBase>
                  <Typography
                    variant="caption"
                    sx={{ fontSize: "0.8rem", padding: 1 }}
                  >
                    {CHARACHTER_LIMIT - inputText.length}
                  </Typography>
                </Box>
              )}

            <TextField
              multiline={true}
              maxRows={2}
              sx={{
                marginTop: "0.2rem",
                fontSize: "8px",
                background: theme.palette.background.paper,
                "& .MuiInputBase-input": {
                  padding: "0px",
                  overflow: "scroll",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                },
                backgroundColor:
                  theme.palette.mode === "dark" ? "#2A2A2A" : "#F4F2FF",
              }}
              fullWidth
              placeholder="Type your message..."
              value={inputText}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length > CHARACHTER_LIMIT) {
                  showErrorMessage(
                    `Message should be ${CHARACHTER_LIMIT} characters or less`
                  );
                  setInputText(value.slice(0, CHARACHTER_LIMIT + 1));
                } else {
                  setInputText(value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend(inputText);
                  setInputText("");
                }
              }}
              slotProps={{
                htmlInput: { style: { fontSize: "14px" } },
                input: {
                  style: {
                    fontSize: "14px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#2A2A2A" : "#fff",
                    padding: "4px",
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        sx={{
                          color:
                            inputText?.length === 0 ||
                            inputText?.length > CHARACHTER_LIMIT
                              ? "#B0B0B0"
                              : "#7567F8",
                        }}
                        disabled={
                          inputText?.length === 0 ||
                          inputText?.length > CHARACHTER_LIMIT
                        }
                        onClick={() => {
                          handleSend(inputText);
                          setInputText("");
                        }}
                      >
                        <SendIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default observer(AllConversation);
